import { apiRequest } from './http';
import type { ApiItemResponse, ApiListResponse, GovernanceLifecycleState, TemplateRecord, TemplateVersionRecord } from '../types/api';
import type { LifecycleAction } from './mappings';

type Query = Record<string, string | number | boolean | undefined>;

const actionState: Record<LifecycleAction, GovernanceLifecycleState> = {
  validate: 'VALIDATED',
  approve: 'APPROVED',
  activate: 'ACTIVE',
  reject: 'DRAFT',
};

function queryString(query: Query = {}): string {
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') search.set(key, String(value));
  });
  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}

export const templatesApi = {
  list: (query: Query = {}) => apiRequest<ApiListResponse<TemplateRecord>>(`/templates${queryString(query)}`),
  getTemplate: async (id: string) => (await apiRequest<ApiItemResponse<TemplateRecord>>(`/templates/${id}`)).data,
  createTemplate: async (payload: Omit<TemplateRecord, 'id' | 'createdAt' | 'updatedAt'>) =>
    (await apiRequest<ApiItemResponse<TemplateRecord>>('/templates', { method: 'POST', body: JSON.stringify(payload) })).data,
  deleteTemplate: async (id: string) => apiRequest<void>(`/templates/${id}`, { method: 'DELETE' }),
  listTemplateVersions: async (id: string) =>
    (await apiRequest<ApiItemResponse<TemplateVersionRecord[]>>(`/templates/${id}/versions`)).data,
  createTemplateVersion: async (
    id: string,
    payload: { templateBody?: string; defaultSourcePayload?: Record<string, unknown>; description?: string; changeLog?: string; versionLabel?: string },
  ) =>
    (await apiRequest<ApiItemResponse<TemplateRecord>>(`/templates/${id}/versions`, {
      method: 'POST',
      body: JSON.stringify({
        versionLabel: payload.versionLabel,
        description: payload.description,
        templateBody: payload.templateBody,
        defaultSourcePayload: payload.defaultSourcePayload,
        lifecycleState: 'DRAFT',
      }),
    })).data,
  updateTemplateVersionLifecycle: async (id: string, versionId: string, action: LifecycleAction) =>
    (await apiRequest<ApiItemResponse<TemplateRecord>>(`/templates/${id}/versions/${versionId}/lifecycle`, {
      method: 'PATCH',
      body: JSON.stringify({ lifecycleState: actionState[action] }),
    })).data,
};
